import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

// Create the Cart Context
const CartContext = createContext();

// Shipping cost calculation function
const calculateShippingCost = async (origin, destination, weight, courier) => {
  try {
    const response = await axios.get(`https://api.binderbyte.com/v1/cost`, {
      params: {
        api_key: 'ff7e74f59b1147c63c21c9c43058265c6edb8be3b767e4d10b28cbaa96a57010', // Replace with a valid API key
        courier: courier, // Use user's selected courier
        origin: origin,
        destination: destination,
        weight: weight
      }
    });

    // Assuming the API returns shipping costs
    return response.data.data.cost[0].value;
  } catch (error) {
    console.error('Shipping cost calculation error:', error);
    // Fallback shipping cost if API fails
    return 20000; // Default shipping cost in IDR
  }
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const [shippingCost, setShippingCost] = useState(0);
  const [selectedCourier, setSelectedCourier] = useState('jne'); // Default to 'jne'
  const [destination, setDestination] = useState(''); // User input for destination

  const addToCart = (product) => {
    setCart(prevCart => {
      const existingProductIndex = prevCart.findIndex(item => item.id === product.id);
      
      if (existingProductIndex > -1) {
        const updatedCart = [...prevCart];
        updatedCart[existingProductIndex] = {
          ...updatedCart[existingProductIndex],
          quantity: updatedCart[existingProductIndex].quantity + 1
        };
        return updatedCart;
      } else {
        return [...prevCart, { ...product, quantity: 1 }];
      }
    });
  };

  const removeFromCart = (productId) => {
    setCart(prevCart => prevCart.filter(item => item.id !== productId));
  };

  const updateQuantity = (productId, quantity) => {
    setCart(prevCart => 
      prevCart.map(item => 
        item.id === productId 
          ? { ...item, quantity: Math.max(0, quantity) }
          : item
      ).filter(item => item.quantity > 0)
    );
  };

  const calculateTotal = () => {
    const subtotal = cart.reduce((total, item) => total + (item.price * item.quantity), 0);
    return subtotal + shippingCost;
  };

  const calculateSubtotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const clearCart = () => {
    setCart([]);
    setShippingCost(0);
  };

  // Calculate total weight of products
  const calculateTotalWeight = () => {
    return cart.reduce((total, item) => total + (item.weight || 1) * item.quantity, 0);
  };

  // Update shipping cost based on cart and selected courier
  useEffect(() => {
    const updateShippingCost = async () => {
      try {
        // Default origin and the selected courier
        const origin = 'Pati'; // Updated origin city
        const weight = calculateTotalWeight();

        const cost = await calculateShippingCost(origin, destination, weight, selectedCourier);
        setShippingCost(cost);
      } catch (error) {
        // Fallback to default shipping cost
        setShippingCost(20000);
      }
    };

    if (cart.length > 0 && destination) {
      updateShippingCost();
    } else {
      setShippingCost(0);
    }
  }, [cart, selectedCourier, destination]);

  return (
    <CartContext.Provider value={{ 
      cart, 
      addToCart, 
      removeFromCart, 
      updateQuantity, 
      clearCart,
      calculateTotal,
      calculateSubtotal,
      shippingCost,
      selectedCourier,
      setSelectedCourier,
      destination,
      setDestination
    }}>
      {children}
    </CartContext.Provider>
  );
};

// Custom hook to use the Cart Context
export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
